import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { compact } from 'lodash';
import React from 'react';

import { useConfirmation } from 'components/modals/ConfirmationContext';
import { Job, JobArchiveOperation, JobStatus } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { useAppSelector } from 'redux/hooks';
import { useJobArchiveMutation, usePendingJobArchiveOperation } from 'utils/queryHooks/jobs/useJobArchive';
import { usePermissions } from 'utils/usePermissions';

interface JobArchiveControlProps {
  job: Pick<Job, 'name' | 'status' | 'userId' | 'deletedAt'> & { id: string };
  labeled?: boolean;
  /** Extra consequences to state in the confirmation, per operation. */
  confirmationNotes?: Partial<Record<JobArchiveOperation, string>>;
}

const terminalJobStatuses = new Set<string>([
  JobStatus.Completed,
  JobStatus.Failed,
  JobStatus.FailedToStart,
  JobStatus.Cancelled,
]);

const operationConfig = {
  archive: {
    label: 'Archive job',
    Icon: ArchiveOutlinedIcon,
  },
  restore: {
    label: 'Restore job',
    Icon: UnarchiveOutlinedIcon,
  },
} as const;

export const JobArchiveControl: React.FC<JobArchiveControlProps> = ({ job, labeled = false, confirmationNotes }) => {
  const currentUserId = useAppSelector((state) => state.auth.profile?.userId);
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const confirm = useConfirmation();
  const archiveMutation = useJobArchiveMutation();
  const pendingOperation = usePendingJobArchiveOperation(job.id);
  const canManageAllJobs = hasPermission(Permission.ArchiveRestoreAllUsersJobs);
  const canManageOwnJob =
    hasPermission(Permission.ArchiveRestoreOwnJobs) && Boolean(currentUserId) && job.userId === currentUserId;
  const isTerminal = Boolean(job.status && terminalJobStatuses.has(job.status));
  const operation: JobArchiveOperation = pendingOperation || (job.deletedAt ? 'restore' : 'archive');
  const { Icon, label } = operationConfig[operation];
  const isPending = Boolean(pendingOperation);

  if (isLoadingPermissions || !isTerminal || (!canManageAllJobs && !canManageOwnJob)) {
    return null;
  }

  const requestOperation = async () => {
    if (archiveMutation.isPendingForJob(job.id)) {
      return;
    }

    const displayJob = job.name || job.id;
    const confirmed = await confirm({
      title: `${label}?`,
      text: compact([
        operation === 'archive'
          ? `Archive ${displayJob}? The job will leave the Jobs view. Its history and directly produced results remain recoverable.`
          : `Restore ${displayJob}? The job will return to the Jobs view with eligible directly produced results.`,
        confirmationNotes?.[operation],
      ]).join(' '),
      confirmButtonProps: {
        title: operationConfig[operation].label.replace(' job', ''),
        color: operation === 'archive' ? 'warning' : 'primary',
      },
    });

    if (confirmed && !archiveMutation.isPendingForJob(job.id)) {
      archiveMutation.mutate({ jobId: job.id, operation });
    }
  };

  if (labeled) {
    return (
      <Button
        size="small"
        variant="outlined"
        color={operation === 'archive' ? 'warning' : 'primary'}
        startIcon={isPending ? <CircularProgress size={16} /> : <Icon />}
        disabled={isPending}
        onClick={() => void requestOperation()}
      >
        {isPending ? `${operation === 'archive' ? 'Archiving' : 'Restoring'}...` : label}
      </Button>
    );
  }

  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          aria-label={label}
          disabled={isPending}
          onClick={(event) => {
            event.stopPropagation();
            void requestOperation();
          }}
        >
          {isPending ? <CircularProgress size={20} /> : <Icon />}
        </IconButton>
      </span>
    </Tooltip>
  );
};
